/** The /setup-server param that carries the pairing code of the node to claim. */
export const CLAIM_CODE_PARAM = 'code';

/**
 * loginNextTarget — the ?next= value to stamp on a /login redirect for the
 * location being left.
 *
 * /setup-server is special-cased: it is a DERIVED screen (ServerSetupGate
 * routes there while the active org has zero servers), never a meaningful
 * return target in itself — its own ?next param carries the real
 * destination. Capturing the literal /setup-server URL sent re-logins back
 * to the pairing placeholder even when the freshly picked org had servers
 * (observed live: sign out on the placeholder → AuthGate races the menu's
 * navigate('/login') and wins with next=%2Fsetup-server… → the one-click
 * Google button → org chooser → straight back onto the placeholder, looking
 * like the sign-out never happened). Propagating the inner next instead
 * lets the gates re-evaluate the fresh session and route it correctly.
 *
 * A /setup-server URL with a pairing code is the exception: a node's setup
 * page links there to claim that node, so the link is the destination.
 *
 * Pure and dependency-free: shared by AuthGate (React redirect) and the
 * api client's 401 handler (hard redirect), which must not import each
 * other.
 */
export function loginNextTarget(pathname: string, search?: string): string {
    const params = new URLSearchParams(search || '');
    if (pathname === '/setup-server' && params.get(CLAIM_CODE_PARAM) === null) {
        return params.get('next') || '/';
    }
    return pathname + (search || '');
}

/**
 * The /login flag that opens the organization chooser even for a live session whose remembered
 * org would otherwise be entered straight away.
 */
export const CHOOSE_ORG_PARAM = 'choose';

/** The org chooser, returning to `next` once an organization is picked or the pick cancelled. */
export function getOrgChooserUrl(nextUrl: string): string {
    return `/login?${new URLSearchParams({ [CHOOSE_ORG_PARAM]: '1', next: nextUrl })}`;
}
